import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import ApiLoader from "../ui/ApiLoader";
import { getCompanyUsers } from "../../services/axios/AuthApis/TeamCollabApis";
import {
  EnvelopeIcon,
  ClockIcon,
  InformationCircleIcon,
  PhoneIcon,
  PencilSquareIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const CONTACT_TYPES = [
  "Security Operations Center (SOC)",
  "IT Help Desk",
  "Emergency Security Line",
];

const HelpContactsComponent = ({
  emergencyContacts,
  loadingContacts,
  companyEmployees,
  customerMasterId,
  onCreateContact,
  onUpdateContact,
  onDeleteContact,
  contactLogs,
  loadingContactLogs,
  onLoadContactLogs,
  formatDate,
}) => {
  // Security analysts work on the company picked in the company switcher.
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    type: "",
    contact_no: "",
    time: "",
  });
  const [saving, setSaving] = useState(false);
  const [contactError, setContactError] = useState("");

  // Employee search dropdown state
  const [empSearch, setEmpSearch] = useState("");
  const [empResults, setEmpResults] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empPage, setEmpPage] = useState(1);
  const [empTotalPages, setEmpTotalPages] = useState(1);
  const [selectedEmpName, setSelectedEmpName] = useState("");
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const empPageSize = 10;
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch employees from API (same as LMS Assign To)
  const fetchEmployees = async (searchVal, pageNum = 1) => {
    let customerMasterID = customerMasterId;
    if (!customerMasterID){
      customerMasterID = selectedCompany?.company_id;
    }
    try {
      setEmpLoading(true);
      const response = await getCompanyUsers(customerMasterID, pageNum, empPageSize, searchVal);
      if (response.count > 0) {
        setEmpResults(response.results || []);
        setEmpTotalPages(Math.ceil(response.count / empPageSize));
      } else {
        setEmpResults([]);
        setEmpTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmpResults([]);
    } finally {
      setEmpLoading(false);
    }
  };

  // Debounce search (400ms)
  useEffect(() => {
    if (!empSearch.trim()) {
      setEmpResults([]);
      setEmpPage(1);
      setEmpTotalPages(1);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setEmpPage(1);
      fetchEmployees(empSearch, 1);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [empSearch]);

  // Fetch on page change
  useEffect(() => {
    if (empSearch.trim() && empPage > 0) {
      fetchEmployees(empSearch, empPage);
    }
  }, [empPage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowEmpDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectEmployee = (emp) => {
    const name = `${emp.firstname || ""} ${emp.lastname || ""}`.trim();
    setFormData({ ...formData, employee_id: emp.employee_id || emp.user_id });
    setSelectedEmpName(name);
    setEmpSearch("");
    setEmpResults([]);
    setShowEmpDropdown(false);
  };

  const resetEmpDropdown = () => {
    setEmpSearch("");
    setEmpResults([]);
    setEmpPage(1);
    setEmpTotalPages(1);
    setSelectedEmpName("");
    setShowEmpDropdown(false);
  };

  const handleOpenEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      employee_id: contact.employee_id || "",
      type: contact.type || "",
      contact_no: contact.contact_no || "",
      time: contact.time || "",
    });
    setSelectedEmpName(contact.employee_name || "");
    setContactError("");
    setShowEditModal(true);
  };

  const handleOpenAdd = () => {
    setFormData({ employee_id: "", type: "", contact_no: "", time: "" });
    resetEmpDropdown();
    setContactError("");
    setShowAddModal(true);
  };

  const handleSaveEdit = async () => {
    if (!formData.employee_id || !formData.type) return;
    if (formData.contact_no && formData.contact_no.length !== 12) {
      setContactError("Mobile number must be exactly 12 digits");
      return;
    }
    setSaving(true);
    try {
      await onUpdateContact(editingContact.id, formData);
      setShowEditModal(false);
      setEditingContact(null);
    } catch (err) {
      // handled in parent
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNew = async () => {
    if (!formData.employee_id || !formData.type) return;
    if (formData.contact_no && formData.contact_no.length !== 10) {
      setContactError("Mobile number must be exactly 10 digits");
      return;
    }
    setSaving(true);
    try {
      await onCreateContact(formData);
      setShowAddModal(false);
    } catch (err) {
      // handled in parent
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Delete ${contact.type} contact?`)) return;
    await onDeleteContact(contact.id);
  };

  const handleViewLogs = (contact) => {
    onLoadContactLogs(contact.id);
    setEditingContact(contact);
    setShowLogsModal(true);
  };

  // Group contacts by type for display
  const getContactByType = (type) => {
    return emergencyContacts?.find((c) => c.type === type) || null;
  };

  return (
    <motion.div
      key="help"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Emergency Contacts */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Emergency Contacts</h3>
          <button
            onClick={handleOpenAdd}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center text-sm"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Contact
          </button>
        </div>

        {loadingContacts ? (
          <ApiLoader label="Loading contacts..." />
        ) : emergencyContacts && emergencyContacts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {emergencyContacts.map((contact, idx) => (
              <div key={contact.id || idx} className="border border-gray-200 rounded-lg p-4 relative group">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{contact.type}</h4>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleViewLogs(contact)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="View Logs"
                    >
                      <ClockIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenEdit(contact)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Edit"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(contact)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <EnvelopeIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                    <a href={`mailto:${contact.employee_email}`} className="text-red-600 hover:text-red-800 truncate">
                      {contact.employee_email || "Not assigned"}
                    </a>
                  </div>
                  {contact.employee_name && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-4 h-4 mr-2 flex-shrink-0 inline-flex items-center justify-center text-xs font-bold text-gray-400">👤</span>
                      <span>{contact.employee_name}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <PhoneIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                    {contact.contact_no ? (
                      <a href={`tel:${contact.contact_no}`} className="text-red-600 hover:text-red-800">
                        {contact.contact_no}
                      </a>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                    {contact.time || "Not specified"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No emergency contacts configured.</p>
            <p className="text-sm mt-1">Click "Add Contact" to set up your emergency contacts.</p>
          </div>
        )}
      </div>

      {/* Incident Reporting Guidelines */}
      <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <h4 className="text-green-800 dark:text-green-200 font-semibold mb-4">NIST 800-61 Incident Reporting Guidelines</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-semibold text-green-800 dark:text-green-200 mb-2">What to Report</h5>
            <ul className="text-green-700 dark:text-green-300 text-sm space-y-1">
              <li>• Security incidents and threats</li>
              <li>• Data breaches or suspected breaches</li>
              <li>• System compromises</li>
              <li>• Phishing and malware incidents</li>
              <li>• Unauthorized access attempts</li>
            </ul>
          </div>
          <div>
            <h5 className="font-semibold text-green-800 dark:text-green-200 mb-2">How to Report</h5>
            <ul className="text-green-700 dark:text-green-300 text-sm space-y-1">
              <li>• Report incidents immediately</li>
              <li>• Provide detailed descriptions</li>
              <li>• Include timestamps and evidence</li>
              <li>• Follow up if no confirmation received</li>
              <li>• Do not investigate on your own</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-start">
          <InformationCircleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" />
          <div>
            <h4 className="text-yellow-800 dark:text-yellow-200 font-medium">Critical Incident Notice</h4>
            <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
              For immediate security threats, active attacks, or data breaches, call our emergency line immediately. 
              Do not delay reporting critical incidents as this may compromise organizational security.
            </p>
          </div>
        </div>
      </div>

      {/* Add / Edit Contact Modal */}
      <AnimatePresence>
        {(showEditModal || showAddModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    {showEditModal ? "Edit Emergency Contact" : "Add Emergency Contact"}
                  </h2>
                  <button
                    onClick={() => { setShowEditModal(false); setShowAddModal(false); setEditingContact(null); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Contact Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    >
                      <option value="">Select type...</option>
                      {CONTACT_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Employee Selection – Searchable API Dropdown */}
                  <div ref={dropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Employee *</label>

                    {/* Selected employee display */}
                    {formData.employee_id && selectedEmpName ? (
                      <div className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                        <span className="text-sm text-gray-900">{selectedEmpName}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, employee_id: "" });
                            resetEmpDropdown();
                          }}
                          className="text-gray-400 hover:text-red-500 ml-2"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search employee by name..."
                          value={empSearch}
                          onChange={(e) => {
                            setEmpSearch(e.target.value);
                            setShowEmpDropdown(true);
                          }}
                          onFocus={() => { if (empSearch.trim()) setShowEmpDropdown(true); }}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                    )}

                    {/* Dropdown results */}
                    {showEmpDropdown && !formData.employee_id && (
                      <div className="mt-1 border border-gray-200 rounded-lg shadow-lg bg-white max-h-64 overflow-y-auto z-10 relative">
                        {empLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                            <span className="text-sm text-gray-500">Loading employees...</span>
                          </div>
                        ) : empResults.length > 0 ? (
                          <>
                            {empResults.map((emp) => {
                              const name = `${emp.firstname || ""} ${emp.lastname || ""}`.trim();
                              return (
                                <button
                                  key={emp.employee_id || emp.user_id}
                                  type="button"
                                  onClick={() => handleSelectEmployee(emp)}
                                  className="w-full text-left px-3 py-2 hover:bg-red-50 text-sm text-gray-800 border-b border-gray-100 last:border-b-0 transition-colors"
                                >
                                  {name || "Unnamed Employee"}
                                </button>
                              );
                            })}

                            {/* Pagination */}
                            {empTotalPages > 1 && (
                              <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-600">
                                <button
                                  type="button"
                                  disabled={empPage === 1}
                                  onClick={() => setEmpPage((p) => Math.max(1, p - 1))}
                                  className="inline-flex items-center px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                                >
                                  <ChevronLeftIcon className="w-3 h-3 mr-0.5" /> Prev
                                </button>
                                <span>Page {empPage} of {empTotalPages}</span>
                                <button
                                  type="button"
                                  disabled={empPage >= empTotalPages}
                                  onClick={() => setEmpPage((p) => p + 1)}
                                  className="inline-flex items-center px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                                >
                                  Next <ChevronRightIcon className="w-3 h-3 ml-0.5" />
                                </button>
                              </div>
                            )}
                          </>
                        ) : empSearch.trim() ? (
                          <div className="py-4 text-center text-sm text-gray-500">No records found</div>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {/* Contact Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                    <input
                      type="text"
                      value={formData.contact_no}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setFormData({ ...formData, contact_no: val });
                        if (val && val.length !== 10) {
                          setContactError("Mobile number must be exactly 10 digits");
                        } else {
                          setContactError("");
                        }
                      }}
                      maxLength={10}
                      placeholder="e.g. 9876543210"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${
                        contactError ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {contactError && (
                      <p className="text-red-500 text-xs mt-1">{contactError}</p>
                    )}
                    {formData.contact_no && !contactError && (
                      <p className="text-green-600 text-xs mt-1">{formData.contact_no.length}/10 digits</p>
                    )}
                    {formData.contact_no && contactError && (
                      <p className="text-gray-400 text-xs mt-0.5">{formData.contact_no.length}/10 digits</p>
                    )}
                  </div>

                  {/* Availability Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
                    <input
                      type="text"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      placeholder="e.g. 24/7, Mon-Fri 8AM-6PM"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => { setShowEditModal(false); setShowAddModal(false); setEditingContact(null); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={showEditModal ? handleSaveEdit : handleSaveNew}
                    disabled={!formData.employee_id || !formData.type || !formData.contact_no || formData.contact_no.length !== 10 || saving}
                    className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
                    {showEditModal ? "Save Changes" : "Add Contact"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Logs Modal */}
      <AnimatePresence>
        {showLogsModal && editingContact && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[70vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-gray-900">
                    Change History — {editingContact.type}
                  </h2>
                  <button
                    onClick={() => { setShowLogsModal(false); setEditingContact(null); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {loadingContactLogs ? (
                  <ApiLoader label="Loading history..." />
                ) : contactLogs && contactLogs.length > 0 ? (
                  <div className="space-y-3">
                    {contactLogs.map((log, idx) => (
                      <div key={log.id || idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900 capitalize">{log.action}</span>
                          <span className="text-xs text-gray-500">{formatDate ? formatDate(log.created_at) : log.created_at}</span>
                        </div>
                        {log.field_changed && (
                          <p className="text-xs text-gray-600">
                            Field: <span className="font-medium">{log.field_changed}</span>
                          </p>
                        )}
                        {log.old_value && (
                          <p className="text-xs text-gray-500">From: {log.old_value}</p>
                        )}
                        {log.new_value && (
                          <p className="text-xs text-gray-700">To: {log.new_value}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">By: {log.performed_by}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic text-center py-4">No change history available.</p>
                )}

                <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => { setShowLogsModal(false); setEditingContact(null); }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default HelpContactsComponent;
